import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AssociatedItemsList from '../components/associated-block/AssociatedItemsList';
import SearchSelect from './search-select';
import { AddAssociatedComponent } from '../components/associated-block';
import { getFilteredItemsFromClient } from './filterItems';
import { useStateSelector } from '../context/StateSelectors';
import useDebounce from '../hooks/useDebounce';
import {
    fetchFilteredItems,
    fetchItemsByField,
    fetchItemsByIDs
} from './fetchFilteredItems';
import { handleFetchResponse, mergeItems } from './helper';

const SearchBox = React.memo(
    ({
        multiple,
        value,
        icon,
        primaryTitle,
        secondaryTitle,
        items,
        addAssociationLabel,
        translatedSearchLabel,
        wrapperRef,
        handleChange,
        primaryKey,
        secondaryKey,
        primaryType,
        secondaryType,
        totalItemsCount,
        serverSearch = false,
        searchController = '',
        searchFields = [],
        replaceId = null,
        hideSecondaryLabel = false,
        saveFullData = false
    }) => {
        const memoizedItems = useMemo(() => items, [items]);

        const buttonRef = React.useRef(null);

        const [allItems, setAllItems] = useState(memoizedItems);

        const [filteredItems, setFilteredItems] = useState(memoizedItems);
        const [totalCount, setTotalCount] = useState(totalItemsCount);

        const [isFirstSearch, setIsFirstSearch] = useState(true);
        const [openSearch, setOpenSearch] = React.useState(false);

        const [searchTerm, setSearchTerm] = React.useState('');

        const [errorFetch, setErrorFetch] = useState('');

        const server = useStateSelector((state) => state.server);

        const debounceSearchTerm = useDebounce(searchTerm, 700);

        const previousValueRef = React.useRef();

        const fetchAndSetItems = async (val, serverController) => {
            const ids = Array.isArray(val)
                ? val.map((v) => Number(v)).join(',')
                : val;
            let res;
            try {
                if (replaceId) {
                    res = await fetchItemsByField(
                        replaceId,
                        val,
                        serverController
                    );
                } else res = await fetchItemsByIDs(ids, serverController);
                const fetchedItems = handleFetchResponse(res, replaceId);
                setAllItems((prev) => mergeItems(prev, fetchedItems));
            } catch (error) {
                setErrorFetch(error);
            }
        };

        useEffect(() => {
            if (!isValueEmpty && previousValueRef.current === undefined) {
                const serverController = server[searchController];
                fetchAndSetItems(value, serverController);
            }
            previousValueRef.current = value;
        }, [value]);

        useEffect(() => {
            if (!serverSearch) {
                const clientFilteredItems = getFilteredItemsFromClient(
                    searchTerm,
                    memoizedItems,
                    value,
                    primaryKey,
                    secondaryKey
                );
                setFilteredItems(clientFilteredItems);
            }
        }, [searchTerm]);

        useEffect(() => {
            if (
                serverSearch &&
                searchFields &&
                searchController &&
                !isFirstSearch
            ) {
                const serverController = server[searchController];

                fetchFilteredItems(
                    searchFields,
                    debounceSearchTerm,
                    serverController
                )
                    .then((res) => {
                        const result = handleFetchResponse(res, replaceId);
                        setFilteredItems(result);
                        setAllItems((prev) => mergeItems(prev, result));
                        setTotalCount(res?.total_count || 0);
                    })
                    .catch((error) => setErrorFetch(error));
            }
            setIsFirstSearch(false);
        }, [debounceSearchTerm]);

        const handleCancelSelect = () => {
            setOpenSearch(false);
        };
        const handleCloseSelect = (selectedValue) => {
            handleCancelSelect();
            handleChange(selectedValue);
        };
        const getSingleAssociatedItem = (aItems, val) => {
            return aItems.filter((item) => item.id === val) || [];
        };
        const getMultipleAssociatedItems = (aItems, val) => {
            return Array.isArray(val)
                ? aItems.filter((item) => val.includes(item.id))
                : [];
        };

        const selectedItems = useMemo(() => {
            if (multiple) {
                return getMultipleAssociatedItems(allItems, value);
            }
            return getSingleAssociatedItem(allItems, value);
        }, [allItems, value, multiple]);

        const handleSearchChange = (searchValue) => {
            setSearchTerm(searchValue);
        };

        const handleRemove = useCallback(
            (id) => {
                const shortValue = multiple
                    ? value.filter((v) => v !== id)
                    : null;
                const fullValue = multiple
                    ? selectedItems.filter((v) => v.id !== id)
                    : null;
                handleChange(saveFullData ? fullValue : shortValue);
            },
            [multiple, value]
        );

        const isValueEmpty = multiple
            ? value
                ? value.length === 0
                : value === undefined || value === null || value === false
            : !value;

        const showAddButton = multiple || isValueEmpty;

        return (
            <>
                <AssociatedItemsList
                    items={selectedItems}
                    onRemove={(id) => handleRemove(id)}
                    primaryKey={primaryKey}
                    secondaryKey={secondaryKey}
                    primaryType={primaryType}
                    secondaryType={secondaryType}
                    primaryTitle={primaryTitle}
                    secondaryTitle={secondaryTitle}
                    hideSecondaryLabel={hideSecondaryLabel}
                    icon={icon}
                />
                {showAddButton ? (
                    <AddAssociatedComponent
                        onClick={() => setOpenSearch(true)}
                        label={addAssociationLabel}
                        ref={buttonRef}
                    />
                ) : null}

                {openSearch && (
                    <SearchSelect
                        title={translatedSearchLabel}
                        wrapperRef={wrapperRef}
                        buttonRef={buttonRef}
                        onClose={handleCloseSelect}
                        primaryLabel={primaryKey}
                        secondaryLabel={secondaryKey}
                        primaryType={primaryType}
                        secondaryType={secondaryType}
                        items={filteredItems}
                        open={openSearch}
                        multiSelect={multiple}
                        searchTerm={searchTerm}
                        selectedValue={selectedItems}
                        handleSearchChange={(searchVal) =>
                            handleSearchChange(searchVal)
                        }
                        // handleItemSelect={handleItemSelect}
                        searchError={errorFetch}
                        totalCount={totalCount}
                        allItems={allItems}
                        saveFullData={saveFullData}
                    />
                )}
            </>
        );
    }
);

SearchBox.propTypes = {
    translatedLabel: PropTypes.string.isRequired,
    requiredLabel: PropTypes.string,
    multiple: PropTypes.bool,
    value: PropTypes.any,
    icon: PropTypes.node,
    primaryTitle: PropTypes.string,
    secondaryTitle: PropTypes.string,
    addAssociationLabel: PropTypes.string.isRequired,
    translatedSearchLabel: PropTypes.string.isRequired,
    items: PropTypes.array.isRequired,
    wrapperRef: PropTypes.object,
    buttonRef: PropTypes.object,
    primaryContent: PropTypes.string,
    secondaryContent: PropTypes.string
};

export default SearchBox;
